import { Button } from "@/components/flexkit/Button";
import { Alert, AlertDescription, AlertTitle } from "@/components/podkit/alert/Alert";
import { cn } from "@/components/podkit/lib/cn";
import { Pill } from "@/components/podkit/status/Pill";
import { type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Pill component for displaying status labels
 */
export const TierPill: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
    return (
        <Pill variant="neutral" size="sm" className={className}>
            {children}
        </Pill>
    );
};

export type TierCardProps = {
    /**
     * Title of the tier
     */
    title: string;
    description: string;
    icon: ReactNode;
    /**
     * Status pill to display next to the title (e.g., "Active", "Coming soon")
     */
    statusPill?: ReactNode;
    className?: string;
    isActive?: boolean;
    actions?: ReactNode;
};
/**
 * TierCard component for displaying subscription tier information
 */
export const TierCard: FC<TierCardProps> = ({ title, description, icon, statusPill, className, isActive, actions }) => {
    return (
        <Alert
            variant="default"
            className={cn(
                "flex max-w-[350px] flex-col justify-between",
                isActive && "border-brand-foreground",
                className,
            )}
        >
            <div className="flex flex-col gap-2">
                <AlertTitle className="flex items-center gap-1.5">
                    {icon}
                    {title}
                    {statusPill}
                </AlertTitle>

                <AlertDescription className="text-base">{description}</AlertDescription>
            </div>

            {actions && <div className="mt-3 flex gap-2">{actions}</div>}
        </Alert>
    );
};

/**
 * Action button for tier cards
 */
export const TierCardAction: FC<{
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    href?: string;
    to?: string;
    className?: string;
}> = ({ children, onClick, disabled, href, to, className }) => {
    if (href) {
        return (
            <Button variant="secondary" disableTracking className={className} asChild>
                <a href={href} target="_blank" rel="noreferrer">
                    {children}
                </a>
            </Button>
        );
    }

    if (to) {
        return (
            <Button variant="secondary" disableTracking onClick={onClick} className={className} asChild>
                <Link to={to}>{children}</Link>
            </Button>
        );
    }

    return (
        <Button variant="secondary" disableTracking onClick={onClick} disabled={disabled} className={className}>
            {children}
        </Button>
    );
};
